import { useState, useEffect, useCallback } from "react";
import { getStatsHealth, getStatsWorkers } from "../api/adminApi";
import type { StatsHealthResponse, DatabaseStatus, CircuitBreakerStatus, AuditSyncBufferStatus, AuditDLQStatus, StatsWorkersResponse, R2BucketStatus } from "../types/admin";
import { WorkerTimeSeriesCharts } from "../components/WorkerTimeSeriesCharts";
import {
  formatDateTime,
  formatLastUpdated,
  formatRelativeTime as formatRelativeTimeET,
  formatDuration as formatDurationET,
  TIMEZONE_LABEL
} from "../utils/timezone";

/**
 * Circuit Breaker Status Card with visual state indicator
 */
function CircuitBreakerCard({ status, title = "Circuit Breaker" }: { status: CircuitBreakerStatus; title?: string }) {
  const stateConfig: Record<string, { color: string; label: string; icon: string }> = {
    closed: { color: "var(--accent-success)", label: "Healthy", icon: "✓" },
    "half-open": { color: "var(--accent-warning)", label: "Recovering", icon: "◐" },
    open: { color: "var(--accent-danger)", label: "Tripped", icon: "✕" },
    unknown: { color: "#64748b", label: "Unknown", icon: "?" },
    unavailable: { color: "#64748b", label: "Unavailable", icon: "—" },
  };

  // Normalize state to lowercase for lookup (backend may return uppercase)
  const normalizedState = status.state?.toLowerCase() || "unknown";
  const config = stateConfig[normalizedState] || stateConfig.unknown;

  // Format timestamps using East Coast timezone utilities
  const formatTime = (timestamp: number | null) => {
    if (!timestamp) return "Never";
    return formatDateTime(timestamp);
  };

  const formatRelativeTime = formatRelativeTimeET;

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">{title}</h3>
      </div>
      <div className="card-body">
        {/* State indicator */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "1rem",
            marginBottom: "1.5rem",
            padding: "1rem",
            backgroundColor: `${config.color}15`,
            borderRadius: "0.5rem",
            border: `1px solid ${config.color}40`,
          }}
        >
          <div
            style={{
              width: "48px",
              height: "48px",
              borderRadius: "50%",
              backgroundColor: config.color,
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.5rem",
              fontWeight: "bold",
            }}
          >
            {config.icon}
          </div>
          <div>
            <div style={{ fontSize: "0.875rem", color: "#94a3b8" }}>Current State</div>
            <div style={{ fontSize: "1.5rem", fontWeight: 600, color: config.color }}>
              {config.label}
            </div>
          </div>
        </div>

        {/* Stats grid */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
          <div className="stat-item">
            <div style={{ color: "#94a3b8", fontSize: "0.75rem", marginBottom: "0.25rem" }}>
              Failures
            </div>
            <div
              style={{
                fontSize: "1.25rem",
                fontWeight: 600,
                color: status.failureCount > 0 ? "var(--accent-danger)" : "inherit",
              }}
            >
              {Math.floor(status.failureCount)}
            </div>
          </div>
          <div className="stat-item">
            <div style={{ color: "#94a3b8", fontSize: "0.75rem", marginBottom: "0.25rem" }}>
              Successes
            </div>
            <div
              style={{
                fontSize: "1.25rem",
                fontWeight: 600,
                color: status.successCount > 0 ? "var(--accent-success)" : "inherit",
              }}
            >
              {status.successCount}
            </div>
          </div>
          <div className="stat-item">
            <div style={{ color: "#94a3b8", fontSize: "0.75rem", marginBottom: "0.25rem" }}>
              Last Failure
            </div>
            <div style={{ fontSize: "0.875rem" }}>{formatTime(status.lastFailureTime)}</div>
          </div>
          <div className="stat-item">
            <div style={{ color: "#94a3b8", fontSize: "0.75rem", marginBottom: "0.25rem" }}>
              Next Retry In
            </div>
            <div style={{ fontSize: "0.875rem" }}>{formatRelativeTime(status.nextRetryTime)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Database Latency Card with status indicator
 */
function DatabaseCard({ db, name }: { db: DatabaseStatus | null; name: string }) {
  if (!db) {
    return (
      <div className="card" style={{ opacity: 0.6 }}>
        <div className="card-header">
          <h3 className="card-title">{name}</h3>
        </div>
        <div className="card-body">
          <div style={{ textAlign: "center", color: "#64748b", padding: "1rem" }}>
            Not configured
          </div>
        </div>
      </div>
    );
  }

  const statusConfig: Record<string, { color: string; label: string; icon: string }> = {
    healthy: { color: "var(--accent-success)", label: "Healthy", icon: "✓" },
    slow: { color: "var(--accent-warning)", label: "Slow", icon: "⚠" },
    degraded: { color: "var(--accent-danger)", label: "Degraded", icon: "✕" },
    unavailable: { color: "#64748b", label: "Unavailable", icon: "—" },
    timeout: { color: "var(--accent-danger)", label: "Timeout", icon: "⏱" },
  };

  const config = statusConfig[db.status] || statusConfig.unavailable;

  // Latency color based on value and status
  const getLatencyColor = (ms: number | null, status?: string) => {
    if (status === "timeout") return "var(--accent-danger)";
    if (ms === null) return "#64748b";
    if (ms < 50) return "var(--accent-success)";
    if (ms < 100) return "var(--accent-warning)";
    return "var(--accent-danger)";
  };

  return (
    <div className="card">
      <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 className="card-title">{name}</h3>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.5rem",
            padding: "0.25rem 0.75rem",
            borderRadius: "1rem",
            backgroundColor: `${config.color}20`,
            color: config.color,
            fontSize: "0.75rem",
            fontWeight: 600,
          }}
        >
          <span>{config.icon}</span>
          {config.label}
        </span>
      </div>
      <div className="card-body">
        <div style={{ display: "flex", alignItems: "baseline", gap: "0.5rem" }}>
          <span
            style={{
              fontSize: "2.5rem",
              fontWeight: 700,
              color: getLatencyColor(db.latencyMs, db.status),
            }}
          >
            {db.status === "timeout" ? "Timeout" : db.latencyMs ?? "—"}
          </span>
          <span style={{ color: "#94a3b8", fontSize: "1rem" }}>ms</span>
        </div>
        <div style={{ color: "#64748b", fontSize: "0.75rem", marginTop: "0.5rem" }}>
          Response latency
        </div>
      </div>
    </div>
  );
}

/**
 * R2 Bucket Latency Card with status indicator
 */
function R2BucketCard({ bucket, name }: { bucket: R2BucketStatus | null; name: string }) {
  if (!bucket) {
    return (
      <div className="card" style={{ opacity: 0.6 }}>
        <div className="card-header">
          <h3 className="card-title">{name}</h3>
        </div>
        <div className="card-body">
          <div style={{ textAlign: "center", color: "#64748b", padding: "1rem" }}>
            Not configured
          </div>
        </div>
      </div>
    );
  }

  const statusConfig: Record<string, { color: string; label: string; icon: string }> = {
    healthy: { color: "var(--accent-success)", label: "Healthy", icon: "✓" },
    slow: { color: "var(--accent-warning)", label: "Slow", icon: "⚠" },
    degraded: { color: "var(--accent-danger)", label: "Degraded", icon: "✕" },
    unavailable: { color: "#64748b", label: "Unavailable", icon: "—" },
  };

  const config = statusConfig[bucket.status] || statusConfig.unavailable;

  // Latency color based on value (R2 thresholds: <100ms healthy, <300ms slow)
  const getLatencyColor = (ms: number | null) => {
    if (ms === null) return "#64748b";
    if (ms < 100) return "var(--accent-success)";
    if (ms < 300) return "var(--accent-warning)";
    return "var(--accent-danger)";
  };

  return (
    <div className="card">
      <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 className="card-title">{name}</h3>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.5rem",
            padding: "0.25rem 0.75rem",
            borderRadius: "1rem",
            backgroundColor: `${config.color}20`,
            color: config.color,
            fontSize: "0.75rem",
            fontWeight: 600,
          }}
        >
          <span>{config.icon}</span>
          {config.label}
        </span>
      </div>
      <div className="card-body">
        <div style={{ display: "flex", alignItems: "baseline", gap: "0.5rem" }}>
          <span
            style={{
              fontSize: "2.5rem",
              fontWeight: 700,
              color: getLatencyColor(bucket.latencyMs),
            }}
          >
            {bucket.latencyMs ?? "—"}
          </span>
          <span style={{ color: "#94a3b8", fontSize: "1rem" }}>ms</span>
        </div>
        <div style={{ color: "#64748b", fontSize: "0.75rem", marginTop: "0.5rem" }}>
          Response latency (list probe)
        </div>
      </div>
    </div>
  );
}

/**
 * Latency Gauge Visualization (horizontal bar)
 */
function LatencyBar({ label, value, max = 200 }: { label: string; value: number | null; max?: number }) {
  const getColor = (ms: number | null) => {
    if (ms === null) return "#374151";
    if (ms < 50) return "var(--accent-success)";
    if (ms < 100) return "var(--accent-warning)";
    return "var(--accent-danger)";
  };

  const percentage = value === null ? 0 : Math.min((value / max) * 100, 100);
  const color = getColor(value);

  return (
    <div style={{ marginBottom: "1rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem" }}>
        <span style={{ color: "#94a3b8", fontSize: "0.875rem" }}>{label}</span>
        <span style={{ color, fontWeight: 600 }}>{value ?? "—"} ms</span>
      </div>
      <div
        style={{
          height: "8px",
          backgroundColor: "#374151",
          borderRadius: "4px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${percentage}%`,
            backgroundColor: color,
            borderRadius: "4px",
            transition: "width 0.3s ease",
          }}
        />
      </div>
    </div>
  );
}

/**
 * Audit Sync Buffer Card - Shows D1 sync lag and buffer status
 */
function AuditSyncBufferCard({ buffer }: { buffer: AuditSyncBufferStatus }) {
  // Calculate buffer fill percentage
  const fillPercent = Math.round((buffer.entryCount / buffer.maxCapacity) * 100);
  
  // Determine status based on buffer state
  const getStatus = () => {
    if (buffer.entryCount === 0) return { color: "var(--accent-success)", label: "Synced", icon: "✓" };
    if (buffer.entryCount > 1000) return { color: "var(--accent-danger)", label: "Backlog", icon: "⚠" };
    if (buffer.entryCount > 100) return { color: "var(--accent-warning)", label: "Buffering", icon: "◐" };
    return { color: "var(--accent-success)", label: "Healthy", icon: "✓" };
  };

  const status = getStatus();

  // Format timestamps using East Coast timezone utilities
  const formatTime = (timestamp: number | null) => {
    if (!timestamp) return "Never";
    return formatDateTime(timestamp);
  };

  const formatDuration = formatDurationET;

  return (
    <div className="card">
      <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 className="card-title">Audit Sync Buffer</h3>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.5rem",
            padding: "0.25rem 0.75rem",
            borderRadius: "1rem",
            backgroundColor: `${status.color}20`,
            color: status.color,
            fontSize: "0.75rem",
            fontWeight: 600,
          }}
        >
          <span>{status.icon}</span>
          {status.label}
        </span>
      </div>
      <div className="card-body">
        {/* Buffer fill indicator */}
        <div style={{ marginBottom: "1.5rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem" }}>
            <span style={{ color: "#94a3b8", fontSize: "0.875rem" }}>Buffer Usage</span>
            <span style={{ color: status.color, fontWeight: 600 }}>
              {buffer.entryCount.toLocaleString()} / {buffer.maxCapacity.toLocaleString()}
            </span>
          </div>
          <div
            style={{
              height: "12px",
              backgroundColor: "#374151",
              borderRadius: "6px",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${fillPercent}%`,
                backgroundColor: status.color,
                borderRadius: "6px",
                transition: "width 0.3s ease",
                minWidth: buffer.entryCount > 0 ? "4px" : "0",
              }}
            />
          </div>
          <div style={{ color: "#64748b", fontSize: "0.75rem", marginTop: "0.25rem", textAlign: "right" }}>
            {fillPercent}% capacity
          </div>
        </div>

        {/* Stats grid */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
          <div className="stat-item">
            <div style={{ color: "#94a3b8", fontSize: "0.75rem", marginBottom: "0.25rem" }}>
              Sync Lag
            </div>
            <div
              style={{
                fontSize: "1.25rem",
                fontWeight: 600,
                color: buffer.syncLagMinutes && buffer.syncLagMinutes > 30 ? "var(--accent-danger)" :
                       buffer.syncLagMinutes && buffer.syncLagMinutes > 5 ? "var(--accent-warning)" : "inherit",
              }}
            >
              {formatDuration(buffer.syncLagMinutes)}
            </div>
          </div>
          <div className="stat-item">
            <div style={{ color: "#94a3b8", fontSize: "0.75rem", marginBottom: "0.25rem" }}>
              Last Flush
            </div>
            <div style={{ fontSize: "0.875rem" }}>
              {formatTime(buffer.lastFlushAt)}
            </div>
          </div>
          <div className="stat-item">
            <div style={{ color: "#94a3b8", fontSize: "0.75rem", marginBottom: "0.25rem" }}>
              Last Flush Count
            </div>
            <div style={{ fontSize: "1.25rem", fontWeight: 600 }}>
              {buffer.lastFlushCount}
            </div>
          </div>
          <div className="stat-item">
            <div style={{ color: "#94a3b8", fontSize: "0.75rem", marginBottom: "0.25rem" }}>
              Alarm Status
            </div>
            <div
              style={{
                fontSize: "0.875rem",
                color: buffer.alarmScheduled ? "var(--accent-warning)" : "#94a3b8",
              }}
            >
              {buffer.alarmScheduled ? "Scheduled" : "Idle"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Worker Performance Card - Shows Cloudflare Worker metrics
 */
function WorkerPerformanceCard({
  data,
  loading,
  error
}: {
  data: StatsWorkersResponse | null;
  loading: boolean;
  error: string | null;
}) {
  // Format large numbers
  const formatNumber = (n: number) => {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return n.toLocaleString();
  };

  // Get error rate status color
  const getErrorRateColor = (rate: number) => {
    if (rate < 0.01) return "var(--accent-success)"; // < 1%
    if (rate < 0.05) return "var(--accent-warning)"; // < 5%
    return "var(--accent-danger)"; // >= 5%
  };

  // Get latency status color
  const getLatencyColor = (ms: number) => {
    if (ms < 50) return "var(--accent-success)";
    if (ms < 200) return "var(--accent-warning)";
    return "var(--accent-danger)";
  };

  if (loading) {
    return (
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Worker Performance</h3>
        </div>
        <div className="card-body" style={{ display: "flex", justifyContent: "center", padding: "2rem" }}>
          <div className="loading-spinner" style={{ width: "24px", height: "24px" }} />
        </div>
      </div>
    );
  }

  if (error || !data?.available || !data?.metrics) {
    return (
      <div className="card" style={{ opacity: 0.7 }}>
        <div className="card-header">
          <h3 className="card-title">Worker Performance</h3>
        </div>
        <div className="card-body">
          <div style={{ textAlign: "center", color: "#94a3b8", padding: "1rem" }}>
            <div style={{ marginBottom: "0.5rem" }}>📊 Metrics unavailable</div>
            <div style={{ fontSize: "0.75rem", color: "#64748b" }}>
              {error || data?.error || "CF_API_TOKEN not configured"}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const metrics = data.metrics;
  const errorRatePercent = (metrics.errorRate * 100).toFixed(2);

  return (
    <div className="card">
      <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 className="card-title">Worker Performance</h3>
        <span style={{ color: "#64748b", fontSize: "0.75rem" }}>
          Last 24h
        </span>
      </div>
      <div className="card-body">
        {/* Main metrics row */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "1rem", marginBottom: "1.5rem" }}>
          <div className="stat-item" style={{ textAlign: "center" }}>
            <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "var(--accent-primary)" }}>
              {formatNumber(metrics.requests)}
            </div>
            <div style={{ color: "#94a3b8", fontSize: "0.75rem" }}>Total Requests</div>
          </div>
          <div className="stat-item" style={{ textAlign: "center" }}>
            <div style={{
              fontSize: "1.75rem",
              fontWeight: 700,
              color: getErrorRateColor(metrics.errorRate)
            }}>
              {errorRatePercent}%
            </div>
            <div style={{ color: "#94a3b8", fontSize: "0.75rem" }}>Error Rate</div>
          </div>
          <div className="stat-item" style={{ textAlign: "center" }}>
            <div style={{
              fontSize: "1.75rem",
              fontWeight: 700,
              color: getLatencyColor(metrics.cpuTime.p50Ms)
            }}>
              {metrics.cpuTime.p50Ms.toFixed(1)}ms
            </div>
            <div style={{ color: "#94a3b8", fontSize: "0.75rem" }}>CPU P50</div>
          </div>
          <div className="stat-item" style={{ textAlign: "center" }}>
            <div style={{
              fontSize: "1.75rem",
              fontWeight: 700,
              color: getLatencyColor(metrics.duration.p50Ms)
            }}>
              {metrics.duration.p50Ms.toFixed(0)}ms
            </div>
            <div style={{ color: "#94a3b8", fontSize: "0.75rem" }}>Duration P50</div>
          </div>
        </div>

        {/* P99 latencies */}
        <div style={{
          padding: "0.75rem 1rem",
          backgroundColor: "#1e293b",
          borderRadius: "0.5rem",
          marginBottom: "1rem"
        }}>
          <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "0.5rem" }}>P99 Latencies</div>
          <div style={{ display: "flex", gap: "2rem" }}>
            <div>
              <span style={{ color: "#94a3b8", marginRight: "0.5rem" }}>CPU:</span>
              <span style={{ fontWeight: 600, color: getLatencyColor(metrics.cpuTime.p99Ms) }}>
                {metrics.cpuTime.p99Ms.toFixed(1)}ms
              </span>
            </div>
            <div>
              <span style={{ color: "#94a3b8", marginRight: "0.5rem" }}>Duration:</span>
              <span style={{ fontWeight: 600, color: getLatencyColor(metrics.duration.p99Ms) }}>
                {metrics.duration.p99Ms.toFixed(0)}ms
              </span>
            </div>
          </div>
        </div>

        {/* Error breakdown (if any errors) */}
        {metrics.errors > 0 && (
          <div style={{
            padding: "0.75rem 1rem",
            backgroundColor: "rgba(239, 68, 68, 0.1)",
            border: "1px solid rgba(239, 68, 68, 0.3)",
            borderRadius: "0.5rem"
          }}>
            <div style={{ fontSize: "0.75rem", color: "var(--accent-danger)", marginBottom: "0.5rem" }}>
              Error Breakdown ({metrics.errors} total)
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "0.5rem", fontSize: "0.875rem" }}>
              {metrics.errorBreakdown.exceededCPU > 0 && (
                <div><span style={{ color: "#94a3b8" }}>CPU Exceeded:</span> {metrics.errorBreakdown.exceededCPU}</div>
              )}
              {metrics.errorBreakdown.exceededMemory > 0 && (
                <div><span style={{ color: "#94a3b8" }}>Memory Exceeded:</span> {metrics.errorBreakdown.exceededMemory}</div>
              )}
              {metrics.errorBreakdown.internal > 0 && (
                <div><span style={{ color: "#94a3b8" }}>Internal:</span> {metrics.errorBreakdown.internal}</div>
              )}
              {metrics.errorBreakdown.invocationErrors > 0 && (
                <div><span style={{ color: "#94a3b8" }}>Invocation:</span> {metrics.errorBreakdown.invocationErrors}</div>
              )}
            </div>
          </div>
        )}

        {/* Subrequests info */}
        <div style={{
          marginTop: "1rem",
          paddingTop: "0.75rem",
          borderTop: "1px solid #374151",
          display: "flex",
          justifyContent: "space-between",
          fontSize: "0.875rem"
        }}>
          <span style={{ color: "#94a3b8" }}>Subrequests</span>
          <span style={{ fontWeight: 600 }}>{formatNumber(metrics.subrequests)}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Audit DLQ Card - Shows dead letter queue stats
 * Any entries here indicate audit entries that failed all fallbacks
 */
function AuditDLQCard({ dlq }: { dlq: AuditDLQStatus }) {
  // Determine status based on DLQ state
  const getStatus = () => {
    if (dlq.totalReceived === 0) return { color: "var(--accent-success)", label: "Clear", icon: "✓" };
    if (dlq.failedPermanentlyCount > 0) return { color: "var(--accent-danger)", label: "Failures", icon: "✕" };
    if (dlq.totalReceived > 0 && dlq.recoveredCount === dlq.totalReceived) return { color: "var(--accent-success)", label: "Recovered", icon: "✓" };
    return { color: "var(--accent-warning)", label: "Processing", icon: "◐" };
  };

  const status = getStatus();

  // Format timestamp using East Coast timezone utilities
  const formatTime = (timestamp: string | null) => {
    if (!timestamp) return "Never";
    return formatDateTime(timestamp);
  };

  return (
    <div className="card">
      <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 className="card-title">Audit Dead Letter Queue</h3>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.5rem",
            padding: "0.25rem 0.75rem",
            borderRadius: "1rem",
            backgroundColor: `${status.color}20`,
            color: status.color,
            fontSize: "0.75rem",
            fontWeight: 600,
          }}
        >
          <span>{status.icon}</span>
          {status.label}
        </span>
      </div>
      <div className="card-body">
        {/* Alert banner if there are failed entries */}
        {dlq.failedPermanentlyCount > 0 && (
          <div
            style={{
              padding: "0.75rem 1rem",
              backgroundColor: "rgba(239, 68, 68, 0.1)",
              border: "1px solid rgba(239, 68, 68, 0.3)",
              borderRadius: "0.5rem",
              marginBottom: "1rem",
              fontSize: "0.875rem",
            }}
          >
            <strong style={{ color: "var(--accent-danger)" }}>⚠ Attention:</strong>{" "}
            <span style={{ color: "#f1f5f9" }}>
              {dlq.failedPermanentlyCount} entries failed recovery. Check Sentry for details.
              Data is safe in R2 and will be synced by daily reconciliation.
            </span>
          </div>
        )}

        {/* Stats grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "1rem" }}>
          <div className="stat-item">
            <div style={{ color: "#94a3b8", fontSize: "0.75rem", marginBottom: "0.25rem" }}>
              Total Received
            </div>
            <div
              style={{
                fontSize: "1.5rem",
                fontWeight: 600,
                color: dlq.totalReceived > 0 ? "var(--accent-warning)" : "inherit",
              }}
            >
              {dlq.totalReceived}
            </div>
          </div>
          <div className="stat-item">
            <div style={{ color: "#94a3b8", fontSize: "0.75rem", marginBottom: "0.25rem" }}>
              Recovered
            </div>
            <div
              style={{
                fontSize: "1.5rem",
                fontWeight: 600,
                color: dlq.recoveredCount > 0 ? "var(--accent-success)" : "inherit",
              }}
            >
              {dlq.recoveredCount}
            </div>
          </div>
          <div className="stat-item">
            <div style={{ color: "#94a3b8", fontSize: "0.75rem", marginBottom: "0.25rem" }}>
              Failed Permanently
            </div>
            <div
              style={{
                fontSize: "1.5rem",
                fontWeight: 600,
                color: dlq.failedPermanentlyCount > 0 ? "var(--accent-danger)" : "inherit",
              }}
            >
              {dlq.failedPermanentlyCount}
            </div>
          </div>
          <div className="stat-item">
            <div style={{ color: "#94a3b8", fontSize: "0.75rem", marginBottom: "0.25rem" }}>
              Recent Entries
            </div>
            <div style={{ fontSize: "1.5rem", fontWeight: 600 }}>
              {dlq.recentEntryCount}
            </div>
          </div>
        </div>

        {/* Last received */}
        <div style={{ marginTop: "1rem", paddingTop: "1rem", borderTop: "1px solid #374151" }}>
          <div style={{ color: "#94a3b8", fontSize: "0.75rem", marginBottom: "0.25rem" }}>
            Last Received
          </div>
          <div style={{ fontSize: "0.875rem", color: "#f1f5f9" }}>
            {formatTime(dlq.lastReceivedAt)}
          </div>
        </div>

        {/* Note about in-memory stats */}
        <div style={{ marginTop: "1rem", padding: "0.5rem", backgroundColor: "#1e293b", borderRadius: "0.25rem" }}>
          <span style={{ color: "#64748b", fontSize: "0.75rem" }}>
            ℹ️ DLQ stats are in-memory and reset on worker restart. Check Sentry for persistent DLQ tracking.
          </span>
        </div>
      </div>
    </div>
  );
}

function SystemHealth() {
  const [data, setData] = useState<StatsHealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  
  // Worker metrics state (loaded separately)
  const [workerData, setWorkerData] = useState<StatsWorkersResponse | null>(null);
  const [workerLoading, setWorkerLoading] = useState(true);
  const [workerError, setWorkerError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setError(null);
      const result = await getStatsHealth();
      setData(result);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load health data");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadWorkerData = useCallback(async () => {
    try {
      setWorkerError(null);
      const result = await getStatsWorkers(24);
      setWorkerData(result);
    } catch (err) {
      setWorkerError(err instanceof Error ? err.message : "Failed to load worker metrics");
    } finally {
      setWorkerLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    loadWorkerData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      loadData();
      loadWorkerData();
    }, 30000);
    return () => clearInterval(interval);
  }, [loadData, loadWorkerData]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-message">
        <p>{error}</p>
        <button onClick={loadData} style={{ marginTop: "1rem" }}>
          Retry
        </button>
      </div>
    );
  }

  if (!data) return null;

  const { circuitBreaker, auditCircuitBreaker, auditSyncBuffer, auditDLQ, databases, r2Bucket, usage, environment } = data;
  
  // Normalize circuit breaker states to lowercase (backend may return uppercase)
  const normalizedCBState = circuitBreaker.state?.toLowerCase() || "unknown";
  const normalizedAuditCBState = auditCircuitBreaker.state?.toLowerCase() || "unknown";

  // Helper to convert DB status to health indicator status
  const getDbHealthStatus = (status: string): "healthy" | "warning" | "critical" => {
    switch (status) {
      case "healthy":
        return "healthy";
      case "slow":
        return "warning";
      case "unavailable":
        return "warning";
      case "degraded":
      case "timeout":
        return "critical";
      default:
        return "warning";
    }
  };

  return (
    <div>
      <header className="page-header">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h1 className="page-title">System Health</h1>
            <p className="page-subtitle">
              Real-time infrastructure monitoring
              {lastRefresh && (
                <span style={{ marginLeft: "1rem", color: "#64748b" }}>
                    Last updated: {formatLastUpdated(lastRefresh)} {TIMEZONE_LABEL}
                  </span>
              )}
            </p>
          </div>
          <button
            onClick={() => {
              setLoading(true);
              loadData();
            }}
            style={{
              padding: "0.5rem 1rem",
              borderRadius: "0.5rem",
              border: "none",
              backgroundColor: "var(--accent-primary)",
              color: "white",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            <span style={{ fontSize: "1rem" }}>↻</span>
            Refresh
          </button>
        </div>
      </header>

      {/* Environment Info Banner */}
      <div
        className="card"
        style={{
          marginBottom: "1.5rem",
          background: "linear-gradient(135deg, #1e3a5f 0%, #1e293b 100%)",
        }}
      >
        <div className="card-body" style={{ padding: "1rem 1.5rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", gap: "2rem" }}>
              <div>
                <span style={{ color: "#94a3b8", fontSize: "0.75rem" }}>Environment</span>
                <div style={{ fontWeight: 600, textTransform: "capitalize" }}>
                  {environment.name}
                </div>
              </div>
              <div>
                <span style={{ color: "#94a3b8", fontSize: "0.75rem" }}>Worker Version</span>
                <div style={{ fontWeight: 600, fontFamily: "monospace" }}>
                  {environment.workerVersion || "N/A"}
                </div>
              </div>
              <div>
                <span style={{ color: "#94a3b8", fontSize: "0.75rem" }}>Timestamp ({TIMEZONE_LABEL})</span>
                <div style={{ fontWeight: 600 }}>
                  {formatDateTime(data.timestamp)}
                </div>
              </div>
            </div>
            <div
              style={{
                padding: "0.5rem 1rem",
                backgroundColor: "var(--accent-success)20",
                borderRadius: "0.5rem",
                border: "1px solid var(--accent-success)40",
              }}
            >
              <span style={{ color: "#94a3b8", fontSize: "0.75rem" }}>DAU Today</span>
              <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "var(--accent-success)" }}>
                {usage.dauToday.toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Health Status Summary - At top for quick overview */}
      <div className="card" style={{ marginBottom: "1.5rem" }}>
        <div className="card-header">
          <h3 className="card-title">Health Summary</h3>
        </div>
        <div className="card-body">
          <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap" }}>
            <HealthIndicator
              label="Main Circuit Breaker"
              status={normalizedCBState === "closed" ? "healthy" : normalizedCBState === "half-open" ? "warning" : "critical"}
            />
            <HealthIndicator
              label="Audit Circuit Breaker"
              status={normalizedAuditCBState === "closed" ? "healthy" : normalizedAuditCBState === "half-open" ? "warning" : "critical"}
            />
            <HealthIndicator
              label="Audit Sync Buffer"
              status={
                auditSyncBuffer.entryCount === 0 ? "healthy" :
                auditSyncBuffer.entryCount > 1000 ? "critical" : "warning"
              }
            />
            <HealthIndicator
              label="Audit DLQ"
              status={
                auditDLQ.totalReceived === 0 ? "healthy" :
                auditDLQ.failedPermanentlyCount > 0 ? "critical" : "warning"
              }
            />
            <HealthIndicator
              label="Main Database"
              status={getDbHealthStatus(databases.main.status)}
            />
            {databases.pii && (
              <HealthIndicator
                label="PII Database"
                status={getDbHealthStatus(databases.pii.status)}
              />
            )}
            <HealthIndicator
              label="Audit Database"
              status={getDbHealthStatus(databases.audit.status)}
            />
            {databases.invites && (
              <HealthIndicator
                label="Invites Database"
                status={getDbHealthStatus(databases.invites.status)}
              />
            )}
            {databases.analytics && (
              <HealthIndicator
                label="Analytics Database"
                status={getDbHealthStatus(databases.analytics.status)}
              />
            )}
            {r2Bucket && (
              <HealthIndicator
                label="R2 Audit Bucket"
                status={getDbHealthStatus(r2Bucket.status)}
              />
            )}
          </div>
        </div>
      </div>

      {/* Circuit Breakers Section */}
      <h2 style={{ margin: "0 0 1rem", fontSize: "1.25rem", color: "#f1f5f9" }}>
        Circuit Breakers
      </h2>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem", marginBottom: "1.5rem" }}>
        <CircuitBreakerCard status={circuitBreaker} title="Main D1 Circuit Breaker" />
        <CircuitBreakerCard status={auditCircuitBreaker} title="Audit D1 Circuit Breaker" />
      </div>

      {/* Audit System Status Section */}
      <h2 style={{ margin: "2rem 0 1rem", fontSize: "1.25rem", color: "#f1f5f9" }}>
        Audit System Status
      </h2>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem", marginBottom: "1.5rem" }}>
        <AuditSyncBufferCard buffer={auditSyncBuffer} />
        <AuditDLQCard dlq={auditDLQ} />
      </div>

      {/* Database Status Cards */}
      <h2 style={{ margin: "2rem 0 1rem", fontSize: "1.25rem", color: "#f1f5f9" }}>
        Database Latencies
      </h2>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "1.5rem" }}>
        <DatabaseCard db={databases.main} name="Main Database" />
        <DatabaseCard db={databases.pii} name="PII Database" />
        <DatabaseCard db={databases.audit} name="Audit Database" />
        <DatabaseCard db={databases.invites} name="Invites Database" />
        <DatabaseCard db={databases.analytics} name="Analytics Database" />
      </div>

      {/* R2 Storage Status */}
      <h2 style={{ margin: "2rem 0 1rem", fontSize: "1.25rem", color: "#f1f5f9" }}>
        Object Storage
      </h2>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "1.5rem" }}>
        <R2BucketCard bucket={r2Bucket} name="Audit Bucket" />
      </div>

      {/* Latency Comparison Chart */}
      <div className="card" style={{ marginTop: "1.5rem" }}>
        <div className="card-header">
          <h3 className="card-title">Latency Overview</h3>
        </div>
        <div className="card-body">
          <LatencyBar label="Main DB" value={databases.main.latencyMs} />
          <LatencyBar
            label="PII DB"
            value={databases.pii?.latencyMs ?? null}
          />
          <LatencyBar label="Audit DB" value={databases.audit.latencyMs} />
          <LatencyBar
            label="Invites DB"
            value={databases.invites?.latencyMs ?? null}
          />
          <LatencyBar
            label="Analytics DB"
            value={databases.analytics?.latencyMs ?? null}
          />
          <LatencyBar
            label="R2 Audit Bucket"
            value={r2Bucket?.latencyMs ?? null}
            max={400}
          />
        </div>
      </div>

      {/* Worker Performance Section */}
      <h2 style={{ margin: "2rem 0 1rem", fontSize: "1.25rem", color: "#f1f5f9" }}>
        Worker Performance
      </h2>
      <WorkerPerformanceCard
        data={workerData}
        loading={workerLoading}
        error={workerError}
      />

      {/* Worker Time-Series Charts */}
      <div style={{ marginTop: "1.5rem" }}>
        <WorkerTimeSeriesCharts />
      </div>
    </div>
  );
}

/**
 * Health Indicator Component
 */
function HealthIndicator({
  label,
  status,
}: {
  label: string;
  status: "healthy" | "warning" | "critical";
}) {
  const statusConfig = {
    healthy: { color: "var(--accent-success)", icon: "✓", bg: "rgba(34, 197, 94, 0.1)" },
    warning: { color: "var(--accent-warning)", icon: "⚠", bg: "rgba(234, 179, 8, 0.1)" },
    critical: { color: "var(--accent-danger)", icon: "✕", bg: "rgba(239, 68, 68, 0.1)" },
  };

  const config = statusConfig[status];

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
        padding: "0.75rem 1rem",
        backgroundColor: config.bg,
        borderRadius: "0.5rem",
        border: `1px solid ${config.color}30`,
      }}
    >
      <span
        style={{
          width: "24px",
          height: "24px",
          borderRadius: "50%",
          backgroundColor: config.color,
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.75rem",
          fontWeight: "bold",
        }}
      >
        {config.icon}
      </span>
      <span style={{ color: "#f1f5f9" }}>{label}</span>
    </div>
  );
}

export default SystemHealth;
